import React, { ReactNode } from 'react'

import {
  Dropdown,
  SelectDropdownLinkItem,
  SelectDropdownActionDropdown,
  useModalContext,
} from '@sketch/components'

import { useToast } from '@sketch/toasts'
import { routes, DroppableLinkItem } from '@sketch/modules-common'

import { icons } from './icons'

import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { ProjectsMenu } from '../../../projects/components/ProjectsMenu'
import { EmptyTrashModal } from '../../../shares/components/EmptyTrashModal'
import { checkCommonPermissionChecks } from '../../../projects/components/ProjectDropdown/checkCommonPermissionChecks'
import { TouchOnlyCollapsibleWrapper } from '../../../projects/components/ProjectListItem'
import { DeleteStorageItemsModal } from '../../../storageItems'

export interface MenuItem {
  item: ReactNode
  hidden?: boolean
  key: string
  isActive?: boolean
}

export const menuItemsText = {
  account: 'Account',
  updates: 'Updates',
  settings: 'People & Settings',
  people: 'People',
  allDocuments: 'All Documents',
  sharedWithMe: 'Shared with Me',
  libraries: 'Libraries',
  templates: 'Templates',
  trash: 'Trash',
  drafts: 'My Drafts',
  help: 'Help with Sketch',
  discover: 'Design Resources',
  archive: 'Archive',
}

type WorkspaceMenuItemsProps = {
  workspace: WorkspaceMinimalFragment
  hasDraftsProject: boolean
  canUserAdministerShares: boolean
  isArchiveFeatureEnabled: boolean
  isCurrentProjectArchived: boolean
}

interface MenuItemProps {
  workspace: WorkspaceMinimalFragment
}

const MyDraftsMenuItem: React.FCC<MenuItemProps> = ({ workspace }) => {
  return <ProjectsMenu workspace={workspace} showDraftsProjects />
}

const TrashMenuItem: React.FCC<MenuItemProps> = ({ workspace }) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const isAdmin = workspace.userRole === 'ADMIN'

  return (
    <DroppableLinkItem
      icon={icons.Trash}
      text={menuItemsText.trash}
      to={routes.WORKSPACE_TRASH.create({ workspaceId: workspace.identifier })}
      action={
        isAdmin ? (
          <SelectDropdownActionDropdown
            text="Trash Options"
            content={
              <>
                <Dropdown.Header>Trash</Dropdown.Header>
                <Dropdown.Item
                  intent="negative"
                  onClick={e => {
                    e.preventDefault()
                    showModal(EmptyTrashModal, {
                      workspaceId: workspace.identifier,
                    })
                  }}
                >
                  Empty&hellip;
                </Dropdown.Item>
              </>
            }
          />
        ) : null
      }
      // To align dropdown actions location on mobile phones with all other actions
      additional={<TouchOnlyCollapsibleWrapper disabled as="div" />}
      onStorageItemsDropped={(draggedItem, relatableItems) => {
        const notEnoughPermissions = relatableItems.find(item => {
          if (item.__typename === 'Project') {
            return !checkCommonPermissionChecks({
              workspace,
              project: item,
            }).showEditActions
          }

          return !item.userCanUpdateSettings
        })

        if (notEnoughPermissions) {
          return showToast(
            'Some items could not be trashed due to insufficient permissions.',
            'negative'
          )
        }

        showModal(DeleteStorageItemsModal, {
          items: relatableItems,
          workspaceId: workspace.identifier,
        })
      }}
    />
  )
}

export const getIsActive = (route: string) => window.location.pathname === route

export const getWorkspaceMenuItems = ({
  workspace,
  hasDraftsProject,
  canUserAdministerShares,
  isArchiveFeatureEnabled,
  isCurrentProjectArchived,
}: WorkspaceMenuItemsProps): MenuItem[] => {
  const isFinance = workspace.userRole === 'FINANCE'
  const workspaceId = workspace.identifier

  return [
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.AllDocuments}
          text={menuItemsText.allDocuments}
          to={routes.WORKSPACE_SHARES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'all-documents',
      isActive: getIsActive(routes.WORKSPACE_SHARES.create({ workspaceId })),
    },
    {
      item: <MyDraftsMenuItem workspace={workspace} />,
      hidden: isFinance || !hasDraftsProject,
      key: 'drafts',
      isActive: getIsActive(routes.WORKSPACE_DRAFTS.create({ workspaceId })),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.SharedWithMe}
          text={menuItemsText.sharedWithMe}
          to={routes.WORKSPACE_SHARED_WITH_ME.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'shared-with-me',
      isActive: getIsActive(
        routes.WORKSPACE_SHARED_WITH_ME.create({ workspaceId })
      ),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.LinkLibrary}
          text={menuItemsText.libraries}
          to={routes.WORKSPACE_LIBRARIES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'libraries',
      isActive: getIsActive(routes.WORKSPACE_LIBRARIES.create({ workspaceId })),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.TemplateIcon}
          text={menuItemsText.templates}
          to={routes.WORKSPACE_TEMPLATES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'templates',
      isActive: getIsActive(routes.WORKSPACE_TEMPLATES.create({ workspaceId })),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.Archive}
          text={menuItemsText.archive}
          to={routes.WORKSPACE_ARCHIVE.create({ workspaceId })}
          data-testid="sidebar-archived"
          isActive={match => !!match || isCurrentProjectArchived}
        />
      ),
      hidden: isFinance || !isArchiveFeatureEnabled,
      key: 'archive',
      isActive:
        getIsActive(routes.WORKSPACE_ARCHIVE.create({ workspaceId })) ||
        isCurrentProjectArchived,
    },
    {
      item: <TrashMenuItem workspace={workspace} />,
      hidden: isFinance || !canUserAdministerShares,
      key: 'trash',
      isActive: getIsActive(routes.WORKSPACE_TRASH.create({ workspaceId })),
    },
  ]
}
